import React from 'react';

import { X } from 'lucide-react';

import { cn } from '../../lib/utils';

interface OnboardingCardProps {
  children: React.ReactNode;
  className?: string;
  dismissDisabled?: boolean;
  dismissLabel: string;
  onDismiss?: (() => void) | null;
  title: string;
}

/**
 * Shared chrome for the empty-state onboarding cards. Lifted verbatim out of
 * WorkspaceBackgroundPicker so the three cards in the deck cannot drift apart.
 */
export function OnboardingCard({ children, className, dismissDisabled = false, dismissLabel, onDismiss, title }: OnboardingCardProps) {
  return (
    <div className={cn('w-full max-w-118', className)}>
      <section className="flex w-full flex-col overflow-hidden rounded-2xl border-[0.5px] border-white bg-gray-100 shadow-[0px_4px_20px_0px_rgba(0,0,0,0.16)] dark:border-white/10 dark:bg-neutral-900">
        <div className="flex w-full items-center justify-between px-4 pt-4">
          <h2 className="truncate text-sm/5 font-medium text-black dark:text-neutral-100">{title}</h2>
          {onDismiss ? (
            <button
              type="button"
              data-app-region="none"
              aria-label={dismissLabel}
              disabled={dismissDisabled}
              onClick={onDismiss}
              className="flex size-6 cursor-pointer items-center justify-center text-gray-500 hover:text-black disabled:cursor-not-allowed disabled:opacity-40 dark:text-neutral-400 dark:hover:text-neutral-100"
            >
              <X size={16} strokeWidth={1.5} absoluteStrokeWidth />
            </button>
          ) : null}
        </div>
        {children}
      </section>
    </div>
  );
}

interface OnboardingCardTileProps {
  accessibleLabel: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}

/**
 * The 4th "do the real thing" tile each card ends with (upload, invite, browse widgets).
 * Matched class-for-class to the background picker's upload tile, which is the canonical
 * one: gray-200 surface, gray-400 glyph, hover dims by a brightness step rather than a
 * gray-to-gray swap. Pair it with a lucide icon at size 18 / strokeWidth 1.5.
 */
export function OnboardingCardTile({ accessibleLabel, children, className, disabled = false, onClick }: OnboardingCardTileProps) {
  return (
    <button
      type="button"
      data-app-region="none"
      aria-label={accessibleLabel}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'relative flex h-19 w-full min-w-0 cursor-pointer items-center justify-center overflow-hidden rounded-lg border-[0.5px] border-white bg-gray-200 text-gray-400 transition-[background-color,filter] hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-70 dark:border-white/10 dark:bg-neutral-800 dark:text-neutral-400',
        className,
      )}
    >
      {children}
    </button>
  );
}

/** Grid the tiles sit in — 4 up, matching the background picker's row. */
export function OnboardingCardGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid w-full grid-cols-4 gap-2 p-4">{children}</div>;
}
